import {
  computed,
  onUnmounted,
  onUpdated,
  Ref,
  ref,
  useRouter,
  watch,
} from '@nuxtjs/composition-api';
import { Pagination } from '~/graphql/graphqlOperations';

/**
 * 引数オプション型
 * @property {number} initPage 初期ページ
 * @property {number} rootMargin 上部余白(px)
 * @property {string} pageSelector ページ変更の監視用セレクター
 * @property {string} itemSelector データ要素セレクター
 * @property {Ref<boolean>} initLoading 初期ローディング
 * @property {Ref<HTMLElement | null>} stockedElement stockedItemを囲っている親Element
 * @property {number} heightForDomLoadedJudging Dom更新チェック用の高さ
 * @property {boolean} usePaging ページング使用するかどうかのフラグ
 *
 */
export interface UsePaginationOptions {
  initPage: number;
  rootMargin?: number;
  pageSelector?: string;
  itemSelector?: string;
  initLoading?: Ref<boolean>;
  stockedElement?: Ref<HTMLElement | null>;
  heightForDomLoadedJudging?: number;
  usePaging?: boolean;
}

/**
 * 無限スクロール読み込みの方向
 * none: 初期表示
 * next: 次ページ
 * prev: 前ページ
 */
type LoadingDirection = 'none' | 'next' | 'prev';

/**
 * 【ページ情報】
 * key: ページ番号
 * id: {key}ページ目の1件目データのID
 * y: 座標
 */
type PageInformation = {
  [key: number]: { id: string; y: number };
};

// usePaginationの引数初期値
const defaultPageSelector = '[data-id="#id#"]';
const defaultOptions: UsePaginationOptions = {
  initPage: 1,
  rootMargin: 100,
  pageSelector: defaultPageSelector,
  heightForDomLoadedJudging: 100,
  usePaging: true,
};

/**
 * 【stock関数のパラメーター】
 * items: 取得したデータの配列
 * pagination: ページネーション情報
 * direction: スクロール方向
 * key: データを一意にするための<T>のプロパティ名
 */
type StockParams<T> = {
  items: Array<T> | null | undefined;
  pagination: Pagination | null | undefined;
  direction: LoadingDirection;
  key: keyof T;
  totalPage: number;
  totalCount: number;
};

/**
 * ページネーション用のビジネスロジック
 * @param {UsePaginationOptions} customOptions オプション
 * @returns
 */
export default function usePagination<T>(customOptions = defaultOptions) {
  const options = { ...defaultOptions, ...customOptions };
  const router = useRouter();
  let initPage = options.initPage; /* 初期表示ページ */
  const currentPage = ref(initPage); /* 現在ページ */
  const nextPageNo = ref(currentPage.value + 1); /* 現在表示ページの1ページ前 */
  const prevPageNo = ref(currentPage.value - 1); /* 現在表示ページの1ページ後 */
  const totalPage = ref(0); /* ページ合計数 */
  const totalCount = ref(0); /* データ合計数 */
  const enableTopLoading = ref(false); /* Top読込み判定フラグ */
  const enableBottomLoading = ref(false); /* Bottom読込み判定フラグ */
  const stockedItems = ref<Array<T>>();
  // 上向き読込みでDOMが更新されたあとに、ページのスクロールトップ位置を再設定するためのフラグ
  let isAdjustScrollTopAfterPrevLoading = false;

  const pageInfo: PageInformation = {};

  // 読込み済みページ情報のページ番号を配列で取得する
  const getStockedPages = () =>
    Object.keys(pageInfo).map((pageNo) => Number(pageNo));

  /*
    新しいページ読み込みされた時に、stockedItemsにデータを追加する。
  */
  const stock = (params: StockParams<T>) => {
    // 現在ページ位置用
    let page = 0;
    stockedItems.value ||= [];
    totalPage.value = params.totalPage; /* 合計ページ数 */
    totalCount.value = params.totalCount; /* 総件数 */
    if (params.pagination) {
      // 指定されたデータのpagination情報を設定する。
      page = params.pagination.currentPage || 0; /* 現在のページ */
    }

    // stockedPagesNoに現在ページが含まれていない場合は、下記処理を実行する。
    if (!getStockedPages().includes(page)) {
      /*
        取得件数が1件以上ある場合、1件目のIDを取得して、現在のページ情報をpageInfoに設定する。
        取得件数が0件の場合は、不正なページ番号と判断し以降の処理を行わない。
      */
      if (params.items?.length) {
        const pageFirstId = String(params.items[0][params.key]);
        pageInfo[page] = { id: pageFirstId, y: -1 };
      } else {
        return true;
      }

      /*
        directionの方向によって、各処理を実行する。
        directionで指定された方向に、これ以上スクロールできるページがないことを返却する。
      */
      let noMorePage = false;
      if (params.direction === 'prev') {
        stockedItems.value.unshift(...params.items);
        prevPageNo.value--;
        // 上向き読み込み後、DOM更新されるとスクロールが一番上に移動する。
        // 元の位置に戻す処理を実行するためのフラグをオンにする。
        isAdjustScrollTopAfterPrevLoading = true;
        // Top読込みの処理をできないようにするためにフラグをオフにする。
        enableTopLoading.value = false;
        noMorePage = prevPageNo.value <= 0;
      } else {
        stockedItems.value.push(...params.items);
        if (params.direction === 'next') {
          nextPageNo.value++;
        }

        noMorePage = nextPageNo.value > totalPage.value;
      }
      return noMorePage;
    }
  };

  // Stockをクリアする。
  const clear = () => {
    initPage = 1;
    nextPageNo.value = initPage + 1;
    prevPageNo.value = initPage - 1;
    totalPage.value = 0;
    totalCount.value = 0;
    enableTopLoading.value = false;
    enableBottomLoading.value = false;
    stockedItems.value = [];
    isAdjustScrollTopAfterPrevLoading = false;

    for (const key in pageInfo) {
      delete pageInfo[key];
    }
  };

  let prevScrollY = 0; /* 直前のscrollTopのy座標 */
  /* 
    一覧のスクロールイベントハンドラー
  */
  const scrollEventHandler = (event: Event) => {
    // @ts-ignore
    const currentScrollY = event.target.scrollTop; /* 現在のscrollTopのy座標 */
    // 上方向にスクロールがされているとき、Top読込み（enableTopLoading）を有効にする。
    enableTopLoading.value ||= prevScrollY > currentScrollY;
    prevScrollY = currentScrollY;

    // URLページ番号を変更する。
    resolvePage(currentScrollY);
  };

  // 一覧Elementにスクロールイベントを追加する。
  const addScrollEventHandler = () => {
    options.stockedElement?.value?.addEventListener(
      'scroll',
      scrollEventHandler
    );
  };

  // 一覧Elementからスクロールイベントを削除する。
  const removeScrollEventHandler = () => {
    options.stockedElement?.value?.removeEventListener(
      'scroll',
      scrollEventHandler
    );
  };

  // yの位置からページ移動しているかをチェックして、移動していたらURL変更する。
  const resolvePage = (y: number) => {
    // ページ情報のy座標を大きい順に確認する。
    // Yがしきい値を超過している場合は、変更後ページに設定する。超過していない場合は、現在ページを設定する。

    const newPage =
      getStockedPages()
        .reverse()
        .find((page) => {
          // しきい値を設定。（各ページの開始Y座標 - 上部余白）
          const thresholdY = pageInfo[page].y - (options.rootMargin || 0);
          return y > thresholdY;
        }) || currentPage.value;

    // ページングを使用。かつ、変更後ページと、現在ページが一致していない場合は、URLを書き換える。
    if (options.usePaging && newPage !== currentPage.value) {
      // スクロールイベントを登録しているとreplaceのタイミングでエラーが発生する。
      // vue-router内のスクロールイベントが影響していると推測。
      // replace前にイベントを削除し、URL変更後にイベントを再登録する。
      removeScrollEventHandler();
      router.replace({ query: { page: `${newPage}` } }, () => {
        currentPage.value = newPage;
        addScrollEventHandler();
      });
    }
  };

  /*
    初回取得データのDOMの表示ができているかを判定する。
  */
  const checkInitItemsLoaded = (
    stockedElement: HTMLElement,
    itemSelector?: string,
    heightForDomLoadedJudging?: number
  ) => {
    let isLoaded = false;
    const heightForJudging = heightForDomLoadedJudging || 0;

    // itemSelectorの指定が
    // ある場合は、1件目のitemSelectorの高さ（clientHeight）が、
    // ない場合は、stockedItemを囲っている親Element（stockedElement）のscrollHeightが、
    // Dom更新チェック用の高さ（heightForJudging）よりも高い場合は、isLoadedでTRUEを返す。
    if (itemSelector) {
      const clientHeight =
        stockedElement.querySelector(itemSelector)?.clientHeight || 0;

      isLoaded = clientHeight > heightForJudging;
    } else {
      isLoaded = stockedElement.scrollHeight > heightForJudging;
    }
    return isLoaded;
  };

  // URL切り替えのために各ページの開始位置を再設定する。
  const resetPageInfo = (stockedElement: HTMLElement, selector?: string) => {
    const pageSelector = selector || defaultPageSelector;

    // 登録済みのページ（stockedPagesNo）を設定する。
    for (const page of getStockedPages()) {
      // 1ページ目は先頭（0）を設定する。
      // 2ページ目以降は各ページ1件目のElementのoffsetTopを設定する。
      if (page === 1) {
        pageInfo[page].y = 0;
      } else {
        const info = pageInfo[page];
        // 各ページ1件目のElementを取得する。
        const element = stockedElement.querySelector(
          pageSelector.replace('#id#', String(info.id))
        ) as HTMLElement;

        // Y座標を設定する。
        if (element) {
          pageInfo[page].y = element.offsetTop;
        }
      }
    }
  };

  // 初期表示が1ページ目以外の場合の表示調整用フラグ（onUpdateで使用）
  let moreThanPage2 = initPage > 1;
  // 画面更新時のリスト高さ更新用
  const updatedListHeight = ref(0);

  // 画面更新時処理
  onUpdated(() => {
    const stockedElement = options.stockedElement?.value;
    if (stockedElement) {
      // スクロールイベントを削除&追加
      removeScrollEventHandler();
      addScrollEventHandler();

      // ページ情報を再設定する
      resetPageInfo(stockedElement, options.pageSelector);

      // 現在のスクロール位置からページ番号を解決する
      resolvePage(stockedElement.scrollTop);

      // 各ページの先頭のY座標を設定
      // stockedElementのリスト高さを設定
      updatedListHeight.value = stockedElement.scrollHeight || 0;

      if (
        checkInitItemsLoaded(
          stockedElement,
          options.itemSelector,
          options.heightForDomLoadedJudging
        )
      ) {
        // Bottom読込みの処理を可能にするためにフラグをオンにする。
        enableBottomLoading.value = true;

        // 初期読込みページが1ページ目ではない場合は、上方向スクロール制御のため、初期表示位置を少し上にずらしておく。
        if (moreThanPage2) {
          stockedElement.scrollTop = 1;
          moreThanPage2 = false;
        }
      }
    }
  });

  // 後処理（スクロールイベント終了する）
  onUnmounted(() => {
    removeScrollEventHandler();
  });

  const spinnerHeight = 64; /* spinnerが表示されている固定の高さ（px） */
  /*
    リストの高さ（updatedListHeight）の変更を監視。
    上向き読込みでDOMが更新されたタイミングで、スクロール位置を調整する。
    現在のスクロール位置（scrollTop）に
    読み込み前の一覧DOMの高さと読み込み後の一覧DOMの高さの差分を加算して、spinnerHeight分の高さを引く。
  */
  watch(updatedListHeight, (newHeight, oldHeight) => {
    const stockedElement = options.stockedElement?.value;
    if (isAdjustScrollTopAfterPrevLoading && stockedElement) {
      stockedElement.scrollTop += newHeight - oldHeight - spinnerHeight;
      isAdjustScrollTopAfterPrevLoading = false;
    }
  });

  return {
    nextPageNo,
    prevPageNo,
    currentPage,
    totalPage,
    totalCount,
    enableTopLoading,
    enableBottomLoading,
    pageInfo,
    stock,
    stockedItems,
    clear,
  };
}
